import axios from 'axios';
import moment from 'moment';
import config from '@/config';
import fs from 'fs';
import CryptoJS from 'crypto-js';
import { VENUE_STORAGE } from '@/constants';

export function imageToDataUri(img, width = null, height = null) {
  return new Promise(resolve => {
    // We create an image to receive the Data URI
    const fakeImg = document.createElement('img');
    fakeImg.onload = function() {
      // create an off-screen canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      // set its dimension to target size
      if (width && height) {
        canvas.width = width;
        canvas.height = height;
        // draw source image into the off-screen canvas
        ctx.drawImage(this, 0, 0, width, height);
      } else {
        canvas.width = this.width;
        canvas.height = this.height;
        // draw source image into the off-screen canvas
        ctx.drawImage(this, 0, 0, this.width, this.height);
      }
      // encode image to data-uri with base64 version of compressed image
      resolve(canvas.toDataURL());
    };
    fakeImg.crossOrigin = 'anonymous';
    fakeImg.src = img;
  });
}

export function generateThumbnailReso(srcWidth, srcHeight, maxWidth = 120, maxHeight = 120) {
  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return { width: srcWidth * ratio, height: srcHeight * ratio };
}

export function getImageDimensions(src) {
  return new Promise(resolve => {
    const i = new Image();
    i.onload = function() {
      resolve({
        width: this.width,
        height: this.height
      });
    };
    i.src = src;
  });
}

export function ifFileExists(path) {
  return new Promise(function(resolve) {
    axios
      .get(path)
      .then(function() {
        resolve({
          success: true
        });
      })
      .catch(function() {
        resolve({
          success: false
        });
      });
  });
}

export function isValidURL(string) {
  var res = string.match(
    /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g
  );
  if (res == null) return false;
  else return true;
}

export function isValidDate(date) {
  if (date == undefined) return false;

  return moment(date).isValid();
}

export function isValidTime(time) {
  if (time == undefined) return false;

  return moment(time, 'HH:mm', true).isValid();
}

export function isValidTimeAllowNull(time) {
  if ([undefined, null, ''].includes(time)) return true;

  const timePattern = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
  if (!timePattern.test(time)) return 'Invalid time format';
  return moment(time, 'HH:mm', true).isValid();
}

export function imageToBase64(url) {
  return axios
    .get(url, {
      responseType: 'arraybuffer'
    })
    .then(response => new Buffer(response.data, 'binary').toString('base64'));
}

export function getSelectedVenue() {
  const selected = localStorage.getItem(VENUE_STORAGE);

  if (selected) {
    try {
      return JSON.parse(selected);
    } catch (e) {
      return null;
    }
  }

  return null;
}

export function folderPathsToTree(folderPathArray, assetsArray) {
  if (!Array.isArray(folderPathArray) || !Array.isArray(assetsArray)) {
    console.error('folderPathsToTree: Invalid input - expected arrays');
    return [];
  }

  const validFolders = folderPathArray
    .filter(folder => folder && folder._id && folder.folderName)
    .map(folder => {
      let normalizedPath = folder.folderPath;
      if (!normalizedPath || normalizedPath.trim() === '') {
        console.warn(
          `Folder "${folder.folderName}" (${folder._id}) has empty folderPath. Using _id as fallback.`
        );
        normalizedPath = folder._id;
      }

      return {
        ...folder,
        folderPath: normalizedPath
      };
    });

  if (validFolders.length === 0) {
    return [];
  }

  const paths = validFolders.map(folder => folder.folderPath);

  return paths.reduce((c, v) => {
    if (!v || typeof v !== 'string') {
      console.warn('Invalid folderPath encountered:', v);
      return c;
    }

    const pathSegments = v.split('/').filter(seg => seg.trim() !== '');

    pathSegments.reduce((a, o, i) => {
      if (i === 0) {
        var p = a;
      } else {
        var p = (a.children = a.children || []);
      }

      let f = p.find(x => x && x.id === o.trim());

      if (!f) {
        let selectedFolder = validFolders.find(folder => folder._id === o.trim());

        if (!selectedFolder) {
          console.warn(`Folder with _id "${o.trim()}" not found. Skipping this branch.`);
          return a || c;
        }

        let filteredAssets = assetsArray
          .filter(asset => {
            if (!asset) return false;

            if (asset.assetsFolderId === selectedFolder._id) {
              return true;
            }

            return false;
          })
          .map(filtered => {
            return {
              id: filtered._id,
              name: filtered.name || '',
              isFolder: false,
              checked: false,
              file: filtered.filename ? filtered.filename.split('.').pop() : '',
              filename: filtered.filename || '',
              duration: filtered.duration || 0,
              created_at: filtered.created_at,
              path: '',
              thumbnailUrl: filtered.thumbnailUrl || null,
              type: filtered.type || '',
              size: filtered.size || 0,
              assetsCount: 0
            };
          });

        f = {
          id: selectedFolder._id,
          name: selectedFolder.folderName.trim(),
          isFolder: true,
          checked: false,
          file: '',
          path: selectedFolder.folderPath,
          thumbnailUrl: null,
          type: 'Folder',
          size: null,
          children: [],
          assetsCount: 0
        };

        if (filteredAssets && filteredAssets.length > 0) {
          f.children.push(...filteredAssets);
          f.assetsCount = filteredAssets.length;
        }

        p.push(f);
      }

      return f || a || c;
    }, c);

    return c;
  }, []);
}

export function formatDate(date, format = 'YYYY-DD-MM') {
  if (!date) {
    return '';
  }

  return moment(date).format(format);
}

export async function checkThumbnailExistsOnServer(thumbNailPathOnServer) {
  try {
    if (!thumbNailPathOnServer) {
      return false;
    } else {
      return thumbNailPathOnServer;
    }
  } catch (error) {
    return false;
  }
}

export function encryptText(text) {
  const encrypted = CryptoJS.AES.encrypt(text, config.server_key).toString();
  return encrypted;
}

export function decryptText(text) {
  const decrypted = CryptoJS.AES.decrypt(text, config.server_key).toString(CryptoJS.enc.Utf8);
  return decrypted;
}

export function hasProperties(obj) {
  for (let key in obj) {
    if (obj.hasOwnProperty(key)) {
      return true;
    }
  }

  return false;
}

// Added prefix 0 if value < 10
function addPrefix(num) {
  return typeof num == 'number' && num < 10 ? `0${num}` : num || '';
}

// Handle convert zulu time to { Date, time }
export function convertZuluTimeToDateTime(strZulu) {
  var date = '';
  var time = '';
  try {
    const strDateTime = new Date(strZulu);
    date = `${addPrefix(strDateTime.getMonth() + 1)}/${addPrefix(
      strDateTime.getDate()
    )}/${strDateTime.getFullYear()}`;
    time = `${addPrefix(strDateTime.getHours())}:${addPrefix(strDateTime.getMinutes())}`;
  } catch (error) {
    console.log(`convertZuluTimeToDateTime ${strZulu} is executed fail.`);
  }

  return { date, time };
}

// Handle convert csv to array of obj
export function csvToArray(csv) {
  var result = [];
  if (typeof csv == 'string') {
    let separator = typeof csv == 'string' && csv.indexOf('\r\n') > 0 ? '\r\n' : '\n';
    var lines = csv.split(separator);
    if (Array.isArray(lines) && lines[0]) {
      var headers = lines[0].split(',');

      for (var i = 1; i < lines.length; i++) {
        var obj = {};

        if (lines[i] == undefined || lines[i].trim() == '') {
          continue;
        }

        var words = lines[i].split(',');
        for (var j = 0; j < words.length; j++) {
          obj[headers[j].trim()] = !isNaN(Number(words[j])) ? Number(words[j]) : words[j];
        }

        result.push(obj);
      }
    }
  }
  console.log('csvToArray: ', result);
  return result;
}
// JSON ARRAY TO CSV
export function jsonToCsv(jsonData) {
  let csv = '';
  // Get the headers
  let headers = Object.keys(jsonData[0]);
  csv += headers.join(',') + '\n';
  // Add the data
  jsonData.forEach(function(row) {
    let data = headers.map(header => JSON.stringify(row[header])).join(','); // Add JSON.stringify statement
    csv += data + '\n';
  });
  return csv;
}

export function getVenueTheme() {
  const venue = JSON.parse(localStorage.getItem(VENUE_STORAGE));
  if (!venue) return null;

  return {
    peterBlue: venue.primaryColor || config.theme.peterBlue,
    sidebarColor: venue.secondaryColor || config.theme.sidebarColor
  };
}



// WEBPACK FOOTER //
// ./src/services/helpers.js